/**
 * Affiliate commerce API client (brand side).
 *
 * A small dedicated client rather than more keys on the shared `ApiService`,
 * for two reasons:
 *
 *  1. `ApiService` appends `app=<appToken>` to every call and addresses
 *     endpoints by key with a single optional path segment. The commerce
 *     routes are REST-shaped (`products/<public_id>/`) and need PATCH, which
 *     that helper does not express cleanly.
 *  2. Keeping this surface in one file makes it obvious what the commerce
 *     module talks to, which matters while the feature is still being wired
 *     up locally.
 *
 * Auth is the SAME Knox token the rest of the app uses, read through the
 * existing helper -- this does not introduce a second auth mechanism.
 */
import axios from 'axios';
import { getSelfToken } from '../../_helpers/auth/secureToken';

const BASE_URL = process.env.REACT_APP_BASEURL;

const client = axios.create({ baseURL: BASE_URL });

client.interceptors.request.use((config) => {
  const token = getSelfToken();
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

/**
 * Turn a DRF error body into something a human can act on.
 *
 * DRF returns `{"field": ["message"]}` for validation errors and
 * `{"detail": "..."}` for permission/auth ones. Showing the raw object in a
 * snackbar is how "[object Object]" ends up in front of a user.
 */
export function readApiError(error, fallback = 'Something went wrong.') {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;

  const firstKey = Object.keys(data)[0];
  if (!firstKey) return fallback;
  const value = data[firstKey];
  const message = Array.isArray(value) ? value[0] : value;
  return firstKey === 'non_field_errors' ? message : `${firstKey}: ${message}`;
}

const COMMERCE = 'brand/commerce';

const CommerceService = {
  // --- dashboard ---
  getSummary: () => client.get(`${COMMERCE}/summary/`),

  // --- catalog ---
  listCategories: () => client.get(`${COMMERCE}/categories/`),
  createCategory: (payload) => client.post(`${COMMERCE}/categories/`, payload),

  listProducts: (params = {}) => client.get(`${COMMERCE}/products/`, { params }),
  getProduct: (publicId) => client.get(`${COMMERCE}/products/${publicId}/`),
  createProduct: (payload) => client.post(`${COMMERCE}/products/`, payload),
  updateProduct: (publicId, payload) =>
    client.patch(`${COMMERCE}/products/${publicId}/`, payload),
  /**
   * Archives rather than destroying -- the backend flips status to ARCHIVED.
   * A product with order history cannot be deleted (OrderItem.product is
   * PROTECT), and one already mirrored by GNCreators would orphan storefronts
   * that reference it.
   */
  archiveProduct: (publicId) => client.delete(`${COMMERCE}/products/${publicId}/`),

  // --- commission ---
  listRules: () => client.get(`${COMMERCE}/commission-rules/`),
  createRule: (payload) => client.post(`${COMMERCE}/commission-rules/`, payload),
  updateRule: (publicId, payload) =>
    client.patch(`${COMMERCE}/commission-rules/${publicId}/`, payload),

  // --- orders ---
  listOrders: (params = {}) => client.get(`${COMMERCE}/orders/`, { params }),

  // --- creator access ---
  /**
   * Who may promote this brand: OPEN (any enrolled creator) or APPROVAL
   * (approved creators only).
   *
   * The setting is stored here but ENFORCED IN GNCREATORS -- that is where
   * creator-facing discovery lives, so it is the only place that can hide a
   * brand's products from a creator's catalog. GNBrand publishes the value in
   * the catalog payload and GNCreators mirrors it. Expect a switch to take
   * effect on the next catalog sync, not instantly.
   */
  getCreatorAccess: () => client.get(`${COMMERCE}/creator-access/`),
  updateCreatorAccess: (creatorAccess) =>
    client.patch(`${COMMERCE}/creator-access/`, { creator_access: creatorAccess }),

  /**
   * Creators who asked to promote this brand, and the brand's decision.
   *
   * Proxied to GNCreators, which is where creators exist — this backend has no
   * creator records at all. A 503 here means that service is unreachable, NOT
   * that there are no requests; the panel must say so rather than render an
   * empty inbox, which would read as "nobody asked".
   */
  listAccessRequests: (statuses = []) =>
    client.get(`${COMMERCE}/creator-access/requests/`, {
      params: statuses.length ? { status: statuses } : undefined,
      // Repeat the key rather than send status[]=A&status[]=B — DRF's
      // getlist() reads the repeated form.
      paramsSerializer: {
        indexes: null,
      },
    }),
  decideAccessRequest: (handlerId, status, note = '') =>
    client.post(`${COMMERCE}/creator-access/requests/`, {
      handler_id: handlerId,
      status,
      note,
    }),

  /**
   * The creator directory — the brand going looking, rather than only
   * answering creators who knocked.
   *
   * Also proxied to GNCreators, so a 503 means the same thing it does above:
   * that service is unreachable, NOT that there are no creators. Rendering an
   * empty list on a 503 would read as "nobody is on this platform".
   */
  searchCreators: (params = {}) =>
    client.get(`${COMMERCE}/creators/`, { params }),
  creatorFacets: () => client.get(`${COMMERCE}/creators/`, { params: { facets: 1 } }),
  /**
   * Invite is a PRE-APPROVAL, not a message: it puts the creator in exactly
   * the state they would have reached by asking and being approved, so they
   * can start promoting immediately and there is no third status to explain.
   */
  inviteCreator: (handlerId, note = '') =>
    client.post(`${COMMERCE}/creators/`, { handler_id: handlerId, note }),
};

export default CommerceService;
